import { v4 as uuidv4 } from 'uuid';

import { fetchPodcastChapterStream, fetchTrackStream } from '@/lib/api';
import { getCookie } from '@/lib/utils/getCookies';
import { getRandomDefaultImage } from '@/lib/utils/getRandomImage';

import type {
  Album,
  Playlist,
  Podcast,
  TrackFileType,
  PersonalPlaylist,
  FavoritesForController,
  RecentsForController,
} from '@/lib/api';
import type { AccessContentPayload } from '@/lib/utils/access';

interface CreditNormalized {
  label: string;
  value: string;
}

export type Chapter = AccessContentPayload & {
  podcast_id: number;
  id: number;
  image_url?: string;
  title: string;
  description?: string;
};

export interface TrackNormalized {
  trackId: string;
  trackComposer: string;
  trackTitle: string;
  trackPerformers: string;
  credits: CreditNormalized[];
  image?: string;
  free_access?: boolean;
  subscription_tier?: 'basic' | 'premium' | 'vip';
  allowed_roles?: string[];
}

export type CompilationType =
  | 'playlist'
  | 'album'
  | 'podcast'
  | 'personal_playlist'
  | 'personal_favourites'
  | 'personal_recently_viewed';
type Slugs = { [key: string]: string };

interface CompilationImageNormalized {
  small: string;
  xlarge: string;
}

export interface CompilationNormalized extends AccessContentPayload {
  id: string;
  type: CompilationType;
  title: string;
  slugs: Slugs;
  images: CompilationImageNormalized;
  trackCount: number;
  tracks: TrackNormalized[];
}

const normalizePersonalRecentlyViewed = (
  compilation: RecentsForController,
): CompilationNormalized => {
  return {
    id: uuidv4(),
    title: 'Recently Viewed',
    type: 'personal_recently_viewed',
    slugs: {},
    images: {
      small: getRandomDefaultImage(),
      xlarge: getRandomDefaultImage(),
    },
    trackCount: compilation.tracks.length,
    tracks: compilation.tracks.map(track => {
      const composer = track.track_metadata.actors.artists[0].name;
      const performers =
        track.track_metadata.actors.artists
          .slice(1)
          .map(actor => actor.name)
          .join(', ') || '';

      return {
        trackId: track.track_metadata.id.toString(),
        trackTitle: track.track_metadata.title,
        trackComposer: composer,
        trackPerformers: performers,
        credits: [
          {
            label: 'Album',
            value: track.track_metadata.album?.title ?? '',
          },
          {
            label: 'Record label',
            value: track.track_metadata.label,
          },
          {
            label: 'Recorded',
            value: track.track_metadata.interpretationYear?.toString(),
          },
        ],
        image: track.track_metadata.album?.pictureUrl,
      };
    }),
  };
};

const normalizePersonalFavourites = (
  compilation: FavoritesForController,
): CompilationNormalized => {
  return {
    id: uuidv4(),
    title: 'Personal Favourites',
    type: 'personal_favourites',
    slugs: {},
    images: {
      small: getRandomDefaultImage(),
      xlarge: getRandomDefaultImage(),
    },
    trackCount: compilation.tracks.length,
    tracks: compilation.tracks.map(track => {
      const composer = track.track_metadata.actors.artists[0].name;
      const performers =
        track.track_metadata.actors.artists
          .slice(1)
          .map(actor => actor.name)
          .join(', ') || '';

      return {
        trackId: track.track_metadata.id.toString(),
        trackTitle: track.track_metadata.title,
        trackComposer: composer,
        trackPerformers: performers,
        credits: [
          {
            label: 'Album',
            value: track.track_metadata.album?.title ?? '',
          },
          {
            label: 'Record label',
            value: track.track_metadata.label,
          },
          {
            label: 'Recorded',
            value: track.track_metadata.interpretationYear?.toString(),
          },
        ],
        image: track.track_metadata.album?.pictureUrl,
      };
    }),
  };
};

const normalizePlaylist = (compilation: Playlist): CompilationNormalized => {
  return {
    id: compilation.id.toString(),
    type: 'playlist',
    title: compilation.playListName,
    slugs: compilation.slugs,
    free_access: compilation.free_access,
    subscription_tier: compilation.subscription_tier,
    allowed_roles: compilation.allowed_roles,
    images: {
      small: `${compilation.imageRootUrl}/small${compilation.imagePath}`,
      xlarge: `${compilation.imageRootUrl}/xlarge${compilation.imagePath}`,
    },
    trackCount: compilation.tracks.length,
    tracks: compilation.tracks.map(track => {
      const composer = track.actors[0]?.name ?? '';
      const performers =
        track.actors
          .slice(1)
          .map(actor => actor.name)
          .join(', ') || '';

      return {
        trackId: track.trackId.toString(),
        trackTitle: track.trackTitle,
        trackComposer: composer,
        trackPerformers: performers,
        credits: [
          {
            label: 'Album',
            value: track.track_metadata.album?.title ?? '',
          },
          {
            label: 'Record label',
            value: track.track_metadata.label,
          },
          {
            label: 'Recorded',
            value: track.recordYear?.toString(),
          },
        ],
      };
    }),
  };
};

const normalizeAlbum = (compilation: Album): CompilationNormalized => {
  return {
    id: compilation.id.toString(),
    type: 'album',
    title: compilation.name,
    slugs: compilation.slugs,
    free_access: compilation.free_access,
    subscription_tier: compilation.subscription_tier,
    allowed_roles: compilation.allowed_roles,
    images: {
      small: `${compilation.imageRootUrl}/small${compilation.imagePath}`,
      xlarge: `${compilation.imageRootUrl}/xlarge${compilation.imagePath}`,
    },
    trackCount: compilation.trackCount,
    tracks: compilation.tracks.map(track => {
      const composer = track.track_metadata.actors.artists[0].name;
      const performers =
        track.track_metadata.actors.artists
          .slice(1)
          .map(actor => actor.name)
          .join(', ') || '';

      return {
        trackId: track.track_metadata.id.toString(),
        trackTitle: track.track_metadata.title,
        trackComposer: composer,
        trackPerformers: performers,
        credits: [
          {
            label: 'Album',
            value: track.track_metadata.album?.title ?? '',
          },
          {
            label: 'Record label',
            value: track.track_metadata.label,
          },
        ],
      };
    }),
  };
};

const normalizePodcast = (compilation: Podcast): CompilationNormalized => {
  return {
    id: compilation.id.toString(),
    type: 'podcast',
    title: compilation.name,
    slugs: compilation.slugs,
    free_access: compilation.free_access,
    subscription_tier: compilation.subscription_tier,
    allowed_roles: compilation.allowed_roles,
    images: {
      small: compilation.image_url,
      xlarge: compilation.image_url,
    },
    trackCount: compilation.chapters.length,
    tracks: compilation.chapters.map(chapter => ({
      trackId: chapter.id.toString(),
      trackTitle: chapter.name,
      trackComposer: compilation.name,
      trackPerformers: '',
      credits: [],
      free_access: chapter.free_access,
      subscription_tier: chapter.subscription_tier,
      allowed_roles: chapter.allowed_roles,
    })),
  };
};

const inferFreeAccess = (
  free_access?: boolean,
  subscription_tier?: string,
  allowed_roles?: string[],
): boolean | undefined => {
  if (free_access !== undefined) return free_access;
  return !subscription_tier && !allowed_roles?.length ? true : undefined;
};

const normalizeCollectionPodcast = (compilation: {
  id: string | number;
  chapters: Chapter[];
  free_access?: boolean;
  subscription_tier?: 'basic' | 'premium' | 'vip';
  allowed_roles?: string[];
}): CompilationNormalized => {
  return {
    id: compilation.id.toString(),
    type: 'podcast',
    title: '',
    slugs: {},
    free_access: inferFreeAccess(
      compilation.free_access,
      compilation.subscription_tier,
      compilation.allowed_roles,
    ),
    subscription_tier: compilation.subscription_tier,
    allowed_roles: compilation.allowed_roles,
    images: {
      small: '',
      xlarge: '',
    },
    trackCount: compilation.chapters.length,
    tracks: compilation.chapters.map(chapter => ({
      trackId: chapter.id.toString(),
      trackTitle: chapter.title,
      trackComposer: '',
      trackPerformers: '',
      credits: [],
      image: chapter.image_url,
      free_access: chapter.free_access,
      subscription_tier: chapter.subscription_tier,
      allowed_roles: chapter.allowed_roles,
    })),
  };
};

export type Compilation =
  | Playlist
  | Album
  | Podcast
  | PersonalPlaylist
  | RecentsForController
  | FavoritesForController;
export type NormalizeCompilationFunction = (
  compilation: Compilation,
) => CompilationNormalized;
export const normalizeCompilation: NormalizeCompilationFunction =
  compilation => {
    if ('type' in compilation && compilation.type === 'personal_favourites') {
      return normalizePersonalFavourites(compilation);
    } else if (
      'type' in compilation &&
      compilation.type === 'personal_recently_viewed'
    ) {
      return normalizePersonalRecentlyViewed(compilation);
    } else if ('playListName' in compilation && 'playListId' in compilation) {
      return normalizePersonalPlaylist(compilation);
    } else if ('playListName' in compilation) {
      return normalizePlaylist(compilation);
    } else if (
      'releaseDate' in compilation &&
      'productDescription' in compilation
    ) {
      return normalizeAlbum(compilation);
    } else if ('radio_permalink' in compilation && 'web_url' in compilation) {
      return normalizePodcast(compilation);
    } else if ('id' in compilation && 'chapters' in compilation) {
      return normalizeCollectionPodcast(
        compilation as { id: string | number; chapters: Chapter[] },
      );
    } else {
      throw new Error('Unknown entity type');
    }
  };
export type AssetNormalized = {
  contentType: CompilationType;
  url: string;
  type: TrackFileType;
  availableTypes: TrackFileType[];
};
export type NormalizeAssetFunction = (
  siteId: string | undefined,
  compilationId: string,
  compilationType: CompilationType,
  assetId: string,
  format: TrackFileType,
) => Promise<AssetNormalized | undefined>;
export const normalizeAsset: NormalizeAssetFunction = async (
  siteId,
  compilationId,
  compilationType,
  assetId,
  format,
) => {
  if (!siteId) return;
  switch (compilationType) {
    case 'personal_favourites':
    case 'personal_recently_viewed': {
      const track = await fetchTrackStream({
        siteId,
        assetId,
        format,
        compilationType,
      });

      return {
        contentType: compilationType,
        url: track.file.url,
        type: track.file.type as TrackFileType,
        availableTypes: track.file.available_types as TrackFileType[],
      };
    }
    case 'playlist':
    case 'personal_playlist':
    case 'album': {
      // TODO: In development, we rely on the mocked API to return the track URL
      // else, we use the authenticated fetch to get the track URL
      // TODO: I feel like the action of querying the API should come from a
      // context or a hook, not from the component itself
      const track = await fetchTrackStream({
        siteId,
        assetId,
        format,
        compilationType,
        compilationId,
      });

      return {
        contentType: compilationType,
        url: track.file.url,
        type: track.file.type as TrackFileType,
        availableTypes: track.file.available_types as TrackFileType[],
      };
    }
    case 'podcast': {
      const locale =
        getCookie('NEXT_LOCALE') || (getCookie('language') as string);

      const chapter = await fetchPodcastChapterStream({
        slugOrId: assetId,
        locale,
      });
      if (!chapter) throw new Error('Chapter not found');

      return {
        contentType: compilationType,
        url: chapter.file.url,
        type: chapter.file.type as TrackFileType,
        availableTypes: chapter.file.available_types as TrackFileType[],
      };
    }
  }
};

const normalizePersonalPlaylist = (
  compilation: PersonalPlaylist,
): CompilationNormalized => ({
  id: compilation.playListId.toString(),
  type: 'personal_playlist',
  title: compilation.playListName,
  slugs: {},
  free_access: compilation.free_access,
  subscription_tier: compilation.subscription_tier,
  allowed_roles: compilation.allowed_roles,
  images: {
    small: '',
    xlarge: '',
  },
  trackCount: compilation.playLists.length,
  tracks: compilation.playLists.map(track => {
    const composer = track.actors[0]?.name ?? '';
    const performers =
      track.actors
        .slice(1)
        .map(a => a.name)
        .join(', ') || '';
    return {
      trackId: track.trackId.toString(),
      trackTitle: track.trackTitle,
      trackComposer: composer,
      trackPerformers: performers,
      credits: [
        { label: 'Album', value: track.track_metadata.album?.title ?? '' },
        { label: 'Record label', value: track.track_metadata.label },
        { label: 'Recorded', value: track.recordYear?.toString() },
      ],
      image:
        track.track_metadata.album?.pictureUrl ||
        track.track_metadata.album?.imagePath,
    };
  }),
});
